<template>
	<RotationLayout
		:rotation="rotation"
		:subtitle="rotation.election_period"
		:return-url="route('association.representatives.rotations.elections.index', rotation.id)"
		:return-label="trans('rotations.elections.title')"
	>
		<template v-if="election.open" #actions>
			<template v-if="election.active">
				<div class="flex items-center gap-2 text-green-600 uppercase animate-pulse">
					<i class="fa-solid fa-circle"/>
					{{ trans('general.on_air') }}
				</div>
				<PrimaryButton :color="Color.Blue" @click.stop="toggle">
					<i class="fa-solid fa-lock-open"/>
				</PrimaryButton>
			</template>
			<template v-else>
				<PrimaryButton :color="Color.Green" @click.stop="ratify">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('assemblies.elections.ratify') }}
				</PrimaryButton>
				<PrimaryButton :color="Color.Blue" @click.stop="toggle">
					<i class="fa-solid fa-lock"/>
				</PrimaryButton>
			</template>
		</template>
		<Card :title="`${trans('assemblies.seats')}: ${election.limit}`">
			<template v-if="canRatify">
				<List>
					<ElectionCandidateComponent v-for="electionCandidate in election.candidates" :key="`election-candidate-${electionCandidate.id}`" :title="electionCandidate.candidate.title" :candidate="electionCandidate" :selected="selected" :election="election" :limit="election.limit"/>
				</List>
				<template v-if="election.closed">
					<Alert :color="Color.Green">{{ trans('assemblies.elections.ratified') }}</Alert>
					<LabeledObject>
						<template #label>{{ trans('assemblies.elections.ratification') }}</template>
						<p>{{ election.ratification }}</p>
					</LabeledObject>
				</template>
				<LabeledObject v-else required>
					<template #label>{{ trans('assemblies.elections.ratification') }}</template>
					<textarea v-model="election.ratification"/>
				</LabeledObject>
			</template>
			<Alert v-else :color="Color.Yellow">{{ trans('enrollments.accreditation.no_permissions') }}</Alert>
		</Card>
	</RotationLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import {onMounted, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {useEchoModel} from '@laravel/echo-vue'
import List from '@/Components/UI/Lists/List.vue'
import Alert from '@/Components/UI/Alerts/Alert.vue'
import {type Election} from '@/Types/Elections/Election'
import RotationLayout from '@/Layouts/RotationLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import {type ElectionVote} from '@/Types/Elections/ElectionVote'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type RotationElection} from '@/Types/Elections/RotationElection'
import ElectionCandidateComponent from '@/Components/Elections/ElectionCandidate.vue'

const props = defineProps<{
	rotation:Rotation,
	users:Array<User>,
	canRatify:boolean,
	selected:Array<number>,
	election:RotationElection,
}>()

const working:Ref<boolean> = ref(false)
const rotation:Ref<Rotation> = ref(props.rotation)
const selected:Ref<Array<number>> = ref(props.selected)
const election:Ref<RotationElection> = ref(props.election)

function ratify():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.elections.update', {rotation: rotation.value.id, election: election.value.id}), {
			selected: selected.value,
			ratification: election.value.ratification,
		}).then(() => {
			showSuccess(trans('general.updated'))
			election.value.closed = true
			election.value.open = false
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function toggle():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.elections.toggle.update', {rotation: rotation.value.id, election: election.value.id})).then(({data}:{data:boolean}) => {
			showSuccess(trans('general.updated'))
			election.value.active = data
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

onMounted(():void => {
	const rotationEchoModel = useEchoModel('elections', election.value.id)
	rotationEchoModel.channel().listen('VoteStored', (event:{vote:ElectionVote<Election<Rotation>, Rotation>}) => {
		if(event.vote.election_type === 'rotationElection'){
			election.value.votes.push(event.vote)
		}
	})
})
</script>